import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import cancelOrderIcon from "../../assets/images/svg/cancel-order.svg";

interface MetaAdsUpdateBillingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    billingName: string;
    email: string;
  }) => Promise<void>;
  currentData: {
    billingName: string;
    email: string;
  };
  isLoading?: boolean;
}

export default function MetaAdsUpdateBillingModal({
  isOpen,
  onClose,
  onSave,
  currentData,
  isLoading = false,
}: MetaAdsUpdateBillingModalProps) {
  const [billingName, setBillingName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{
    billingName?: string;
    email?: string;
  }>({});

  useEffect(() => {
    if (isOpen) {
      setBillingName(currentData.billingName || "");
      setEmail(currentData.email || "");
      setErrors({});
    }
  }, [isOpen, currentData]);

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!billingName.trim()) {
      newErrors.billingName = "Billing name is required";
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    await onSave({
      billingName: billingName.trim(),
      email: email.trim(),
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/75 backdrop-blur-md flex items-center justify-center lg:p-4 p-2 sm:p-6 overflow-y-auto">
      <div className="relative w-full max-w-[480px] sm:max-w-[500px] py-[10px] px-[20px] my-auto rounded-[30px] sm:rounded-[40px] overflow-hidden bg-[#0e0e10]">
        {/* Background gradients */}
        <div
          className="absolute inset-x-0 top-0 h-[220px] pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 65% at 60% 0%, rgba(27,118,255,0.85) 0%, rgba(27,118,255,0.32) 42%, rgba(9,11,18,0) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(6,13,30,0.38) 0%, rgba(9,11,18,0) 32%, rgba(9,11,18,0.35) 100%)",
          }}
        />

        <div className="relative z-10 lg:px-3 pt-7 pb-7 sm:pb-[34px]">
          {/* Header */}
          <div className="relative mb-5">
            {/* Desktop Layout */}
            <div className="flex items-center justify-between">
              <h2 className="text-white text-[24px] lg:text-[36px] font-semibold">
                Update Billing Details
              </h2>

              <button
                type="button"
                onClick={onClose}
                aria-label="Close modal"
                className="hidden sm:block text-white lg:text-[32px] text-[24px] mt-[-7px] lg:mt-0 leading-none mt-1 flex-shrink-0"
                disabled={isLoading}
              >
                ✕
              </button>
            </div>

            {/* Mobile Close (Top Right Corner) */}
            <button
              onClick={onClose}
              aria-label="Close modal"
              className="sm:hidden absolute top-[-12px] right-[-2px]"
            >
              <img
                src={cancelOrderIcon}
                alt="Close Icon"
                className="w-[20px] h-[20px]"
              />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Billing Name */}
            <div>
              <label className="block text-white text-[14px] lg:text-[16px] font-medium mb-2">
                Billing Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={billingName}
                onChange={(e) => setBillingName(e.target.value)}
                disabled={isLoading}
                className={`w-full px-4 py-3 rounded-[14px] bg-[#1A1D26] text-white text-[14px] lg:text-[16px] border ${
                  errors.billingName ? "border-red-500" : "border-[#1A1D26]"
                } focus:outline-none focus:border-[#58F2A9] disabled:opacity-50 disabled:cursor-not-allowed`}
                placeholder="Enter billing name"
              />
              {errors.billingName && (
                <p className="text-red-500 text-[12px] mt-1">{errors.billingName}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-white text-[14px] lg:text-[16px] font-medium mb-2">
                Billing Email <span className="text-gray-400">(Optional)</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className={`w-full px-4 py-3 rounded-[14px] bg-[#1A1D26] text-white text-[14px] lg:text-[16px] border ${
                  errors.email ? "border-red-500" : "border-[#1A1D26]"
                } focus:outline-none focus:border-[#58F2A9] disabled:opacity-50 disabled:cursor-not-allowed`}
                placeholder="Enter billing email"
              />
              {errors.email && (
                <p className="text-red-500 text-[12px] mt-1">{errors.email}</p>
              )}
            </div>

            {/* Info Box */}
            <div className="rounded-[14px] bg-white/[0.06] p-4 sm:p-[18px]">
              <p className="text-white text-[12px] lg:text-[14px] font-medium leading-[140%]">
                These details will appear on your invoices. Updates will be
                reflected in future invoices only.
              </p>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-white/12 mt-5" />

            {/* Buttons */}
            <div className="mt-5 sm:mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
              {/* Cancel */}
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="order-2 sm:order-1 w-[70%] sm:w-auto px-[32px] py-[14px] rounded-full bg-white text-black text-[15px] sm:text-[16px] font-semibold hover:bg-white/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>

              {/* Save */}
              <button
                type="submit"
                disabled={isLoading}
                className="order-1 sm:order-2 w-full sm:w-auto px-[32px] py-[14px] rounded-full text-white text-[15px] sm:text-[16px] font-semibold disabled:opacity-70 disabled:cursor-not-allowed hover:opacity-90 transition-all duration-200"
                style={{
                  background:
                    "linear-gradient(272.84deg, #58F2A9 0.97%, #1C3941 99.18%)",
                  border: "1px solid #58F2A9",
                }}
              >
                {isLoading ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                    Saving...
                  </span>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
